/**
 * Manage Users Modal
 * Allows company administrators to view, archive, restore, and delete their Manager and Staff users
 */

import { useEffect, useState } from "react";
import type { Company, UserProfile } from "../../../../../shared/types";
import {
  archiveUser,
  deleteUser,
  getArchivedUsers,
  getCompanyUsers,
  restoreUser,
} from "../../../services/user.service";
import { BaseModal } from "../../common/BaseModal";
import { Tabs } from "../../common/Tabs";
import { UserTable } from "../../common/UserTable";
import { ConfirmationDialog } from "../../ConfirmationDialog";
import { ErrorMessage } from "../../ErrorMessage";

interface ManageUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company;
  onSuccess: () => void;
}

export function ManageUsersModal({
  isOpen,
  onClose,
  company,
  onSuccess,
}: ManageUsersModalProps) {
  const [view, setView] = useState<"active" | "archived">("active");
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    user: UserProfile;
    action: "archive" | "restore" | "delete";
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load users when modal opens or view changes
  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, company.id, view]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Server-side filtering for efficiency (matches Companies pattern)
      const data =
        view === "active"
          ? await getCompanyUsers(company.id, false) // isArchived == false
          : await getArchivedUsers(company.id); // isArchived == true
      setUsers(data);
    } catch (error) {
      console.error("Error loading users:", error);
      setError("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveUser = async (user: UserProfile) => {
    setProcessing(user.uid);
    setError(null);
    try {
      await archiveUser(user.uid, company.id);
      await loadUsers();
      setConfirmAction(null);
      onSuccess();
    } catch (error) {
      console.error("Error archiving user:", error);
      setError("Failed to archive user. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const handleRestoreUser = async (user: UserProfile) => {
    setProcessing(user.uid);
    setError(null);
    try {
      await restoreUser(user.uid, company.id);
      await loadUsers();
      setConfirmAction(null);
      onSuccess();
    } catch (error) {
      console.error("Error restoring user:", error);
      setError("Failed to restore user. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteUser = async (user: UserProfile) => {
    setProcessing(user.uid);
    setError(null);
    try {
      await deleteUser(user.uid, company.id);
      await loadUsers();
      setConfirmAction(null);
      onSuccess();
    } catch (error) {
      console.error("Error deleting user:", error);
      setError("Failed to delete user. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const getConfirmDialogProps = () => {
    if (!confirmAction) return null;
    const { user, action } = confirmAction;

    const actionConfig = {
      archive: {
        title: "Confirm Archive User",
        message: "Are you sure you want to archive this user?",
        confirmLabel: "Archive",
        style: "warning" as const,
        showWarning: false,
        warningMessage: undefined,
      },
      restore: {
        title: "Confirm Restore User",
        message: "Are you sure you want to restore this user?",
        confirmLabel: "Restore",
        style: "success" as const,
        showWarning: false,
        warningMessage: undefined,
      },
      delete: {
        title: "Confirm Delete User",
        message: "Are you sure you want to permanently delete this user?",
        confirmLabel: "Delete",
        style: "danger" as const,
        showWarning: true,
        warningMessage:
          "This will permanently delete the user account and all associated data.",
      },
    };

    const config = actionConfig[action];
    const userDetails = (
      <div style={{ marginTop: "12px" }}>
        <UserTable
          rows={[
            { label: "Name", value: `${user.firstName} ${user.lastName}` },
            { label: "Email", value: user.email },
            {
              label: "Role",
              value: user.userType === "manager" ? "Manager" : "Staff",
            },
          ]}
          columns={[
            {
              render: (row) => (
                <span style={{ color: "#6b7280" }}>{row.label}</span>
              ),
            },
            {
              render: (row) => (
                <span
                  style={{
                    color: "#111827",
                    fontWeight: "500",
                    wordBreak: "break-all",
                  }}
                >
                  {row.value}
                </span>
              ),
            },
          ]}
        />
      </div>
    );

    return {
      ...config,
      message: (
        <>
          {config.message}
          {userDetails}
          {error && (
            <div style={{ marginTop: "16px" }}>
              <ErrorMessage message={error} onDismiss={() => setError(null)} />
            </div>
          )}
        </>
      ),
      onConfirm: () => {
        if (action === "archive") handleArchiveUser(user);
        else if (action === "restore") handleRestoreUser(user);
        else handleDeleteUser(user);
      },
      onCancel: () => setConfirmAction(null),
      loading: processing !== null,
    };
  };

  const dialogProps = getConfirmDialogProps();

  // Main modal
  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Manage Users - ${company.name}`}
        size="large"
        closeOnEscape={!processing && !confirmAction}
        closeOnBackdropClick={!processing && !confirmAction}
        loading={loading}
      >
        <Tabs
          variant="pill"
          tabs={[
            { value: "active", label: "Active Users" },
            { value: "archived", label: "Archived Users" },
          ]}
          active={view}
          onChange={(value) => setView(value as "active" | "archived")}
        />

        {/* User count display */}
        {view === "active" && (
          <div
            style={{
              backgroundColor: "#f3f4f6",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}
            >
              Current Users
            </span>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "700",
                color:
                  company.currentUsers >= company.maxUsers
                    ? "#dc2626"
                    : "#059669",
              }}
            >
              {company.currentUsers} / {company.maxUsers}
            </span>
          </div>
        )}

        {error && !confirmAction && (
          <div style={{ marginBottom: "16px" }}>
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p style={{ color: "#6b7280" }}>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p
              style={{
                color: "#6b7280",
                fontSize: "16px",
                marginBottom: "8px",
              }}
            >
              {view === "active"
                ? "No active users found"
                : "No archived users"}
            </p>
            <p style={{ color: "#9ca3af", fontSize: "14px" }}>
              {view === "active"
                ? "Invite managers and staff members to get started."
                : "Archived users will appear here."}
            </p>
          </div>
        ) : (
          <UserTable
            rows={users}
            columns={[
              { render: (row) => `${row.firstName} ${row.lastName}` },
              { render: (row) => row.email },
              {
                render: (row) => (
                  <span
                    style={{
                      padding: "4px 12px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      minWidth: "64px",
                      textAlign: "center",
                      display: "inline-block",
                      backgroundColor:
                        row.userType === "manager" ? "#dbeafe" : "#d1fae5",
                      color: row.userType === "manager" ? "#1e40af" : "#065f46",
                    }}
                  >
                    {row.userType === "manager" ? "Manager" : "Staff"}
                  </span>
                ),
              },
              {
                render: (row) =>
                  row.phoneNumber
                    ? `${row.countryCode || ""} ${row.phoneNumber}`
                    : "-",
              },
              {
                align: "center",
                render: (row) => (
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "center",
                    }}
                  >
                    {view === "active" ? (
                      <button
                        onClick={() =>
                          setConfirmAction({ user: row, action: "archive" })
                        }
                        disabled={processing !== null}
                        style={{
                          padding: "6px 16px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#fff",
                          backgroundColor: "#f59e0b",
                          border: "none",
                          borderRadius: "6px",
                          outline: "none",
                          cursor: processing ? "not-allowed" : "pointer",
                          opacity: processing ? 0.6 : 1,
                        }}
                      >
                        Archive
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            setConfirmAction({ user: row, action: "restore" })
                          }
                          disabled={processing !== null}
                          style={{
                            padding: "6px 16px",
                            width: "80px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#fff",
                            backgroundColor: "#059669",
                            border: "none",
                            borderRadius: "6px",
                            outline: "none",
                            cursor: processing ? "not-allowed" : "pointer",
                            opacity: processing ? 0.6 : 1,
                          }}
                        >
                          Restore
                        </button>
                        <button
                          onClick={() =>
                            setConfirmAction({ user: row, action: "delete" })
                          }
                          disabled={processing !== null}
                          style={{
                            padding: "6px 16px",
                            width: "80px",
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#fff",
                            backgroundColor: "#dc2626",
                            border: "none",
                            borderRadius: "6px",
                            outline: "none",
                            cursor: processing ? "not-allowed" : "pointer",
                            opacity: processing ? 0.6 : 1,
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}

        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              backgroundColor: "#f3f4f6",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </BaseModal>

      {/* Confirmation Dialog */}
      {dialogProps && (
        <ConfirmationDialog
          isOpen={!!confirmAction}
          title={dialogProps.title}
          message={dialogProps.message}
          confirmLabel={dialogProps.confirmLabel}
          confirmStyle={dialogProps.style}
          onConfirm={dialogProps.onConfirm}
          onCancel={dialogProps.onCancel}
          loading={dialogProps.loading}
          showWarningBanner={dialogProps.showWarning}
          warningMessage={dialogProps.warningMessage}
        />
      )}
    </>
  );
}
